import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { ChevronRight, MoreHorizontal } from 'lucide-react';
import PropTypes from 'prop-types';
import { cn } from '../../lib/utils';

export const Breadcrumb = React.forwardRef(({ ...props }, ref) => (
  <nav ref={ref} aria-label="breadcrumb" {...props} />
));
Breadcrumb.displayName = 'Breadcrumb';

export const BreadcrumbList = React.forwardRef(({ className, ...props }, ref) => (
  <ol
    ref={ref}
    className={cn(
      'm-0 flex list-none flex-wrap items-center gap-1.5 break-words p-0 text-sm text-slate-500 sm:gap-2.5',
      className,
    )}
    {...props}
  />
));
BreadcrumbList.displayName = 'BreadcrumbList';
BreadcrumbList.propTypes = { className: PropTypes.string };

export const BreadcrumbItem = React.forwardRef(({ className, ...props }, ref) => (
  <li
    ref={ref}
    className={cn('inline-flex list-none items-center gap-1.5', className)}
    {...props}
  />
));
BreadcrumbItem.displayName = 'BreadcrumbItem';
BreadcrumbItem.propTypes = { className: PropTypes.string };

export const BreadcrumbLink = React.forwardRef(({ asChild, className, ...props }, ref) => {
  const Comp = asChild ? Slot : 'a';
  return (
    <Comp
      ref={ref}
      className={cn(
        'rounded-sm font-medium text-slate-500 transition-colors hover:text-primary',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        className,
      )}
      {...props}
    />
  );
});
BreadcrumbLink.displayName = 'BreadcrumbLink';
BreadcrumbLink.propTypes = {
  asChild: PropTypes.bool,
  className: PropTypes.string,
};

export const BreadcrumbPage = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    role="link"
    aria-disabled="true"
    aria-current="page"
    className={cn('font-semibold text-slate-900', className)}
    {...props}
  />
));
BreadcrumbPage.displayName = 'BreadcrumbPage';
BreadcrumbPage.propTypes = { className: PropTypes.string };

export const BreadcrumbSeparator = ({
  children, className, ...props
}) => (
  <li
    role="presentation"
    aria-hidden="true"
    className={cn('list-none [&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:text-slate-300', className)}
    {...props}
  >
    {children ?? <ChevronRight />}
  </li>
);
BreadcrumbSeparator.displayName = 'BreadcrumbSeparator';
BreadcrumbSeparator.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

export const BreadcrumbEllipsis = ({ className, ...props }) => (
  <span
    role="presentation"
    aria-hidden="true"
    className={cn('flex h-9 w-9 items-center justify-center', className)}
    {...props}
  >
    <MoreHorizontal className="h-4 w-4" />
    <span className="sr-only">More</span>
  </span>
);
BreadcrumbEllipsis.displayName = 'BreadcrumbEllipsis';
BreadcrumbEllipsis.propTypes = { className: PropTypes.string };
